import { Badge, Box, Flex, Stack } from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { Poi as TPoi } from '../../../generated/types';
import Container from '../../Layout/Container';
import PageHeader from '../../Layout/PageHeader';
import PageImages, { SliderImage } from '../../Common/PageImages';
import PoiTeaser from '../Poi/PoiTeaser';
import HotelTeaser from '../Hotel/HotelTeaser';
import CategoryBadge from '../../Common/CategoryBadge';
import useIsMobile from '../../../hooks/useIsMobile';
import React from 'react';
import ContentTitle from '../../Common/ContentTitle';
import ContentBlock from '../../Common/ContentBlock';
import { secondsToHumanString } from '../../../utils/helper';
import ActivityTeaser from '../Activity/ActivityTeaser';
import TeaserSlider from '../../Common/TeaserSlider';
import { useStore } from '../../../store/provider';
import Wrapper from '../Wrapper';

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

interface PoiProps {
  data: TPoi;
}

const Poi: React.FC<PoiProps> = ({ data }) => {
  const isMobile = useIsMobile();
  const store = useStore();

  const mapMarkers = React.useMemo(() => {
    let markers: any[] = [];
    if (data.nearActivities && store.config.renderConfig.poi.nearActivities) {
      markers = markers.concat(data.nearActivities.items);
    }

    if (data.nearPois && store.config.renderConfig.poi.nearPois) {
      markers = markers.concat(data.nearPois.items);
    }

    if (data.nearHotels && store.config.renderConfig.poi.nearHotels) {
      markers = markers.concat(data.nearHotels.items);
    }

    return markers;
  }, [data]);

  return (
    <Wrapper typename={data.__typename}>
      <Box>
        <PageHeader
          preTitle={<Badge>{data.category?.name}</Badge>}
          title={data.title}
          subTitle={`${data.region?.name}`}
        />

        <Container paddingY={4}>
          <Flex
            flexDirection={{
              base: 'column',
              md: 'column',
              lg: 'row',
            }}
          >
            <Box overflow="hidden" flex="1" p="3" pl="0">
              <PageImages
                images={[
                  data.teaserImage as SliderImage,
                  ...((data.images as SliderImage[]) || []),
                ]}
              />

              <Box marginY={8} alignSelf="flex-start">
                <Stack isInline={!isMobile} spacing={4} justifyContent="center">
                  {data.difficulty && (
                    <CategoryBadge
                      label="Schwierigkeit"
                      value={data.difficulty.name}
                    />
                  )}
                  {data.duration && (
                    <CategoryBadge
                      label="Dauer"
                      value={secondsToHumanString(data.duration)}
                    />
                  )}
                  {data.length && (
                    <CategoryBadge label="Länge" value={`${data.length}km`} />
                  )}
                </Stack>
              </Box>

              {data.body && (
                <ContentBlock title="Beschreibung">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data.body,
                    }}
                  />
                </ContentBlock>
              )}
            </Box>
            {/* <SidebarWrapper /> */}
          </Flex>
        </Container>
        <HotelMap
          scrollWheelZoomDelayed
          scrollWheelZoom={false}
          data={data}
          markers={mapMarkers}
          center={data.location}
        />
        {store.config.renderConfig.poi.nearHotels &&
          data.nearHotels?.total! > 0 && (
            <Container paddingTop={8}>
              <ContentBlock noDivider>
                <TeaserSlider
                  TitleComponent={<ContentTitle title="Hotels in der Nähe" />}
                  CardComponent={HotelTeaser}
                  data={data.nearHotels!.items || []}
                />
              </ContentBlock>
            </Container>
          )}
        {store.config.renderConfig.poi.nearActivities &&
          data.nearActivities?.total! > 0 && (
            <Container paddingTop={8}>
              <ContentBlock>
                <TeaserSlider
                  TitleComponent={
                    <ContentTitle title="Aktivitäten in der Nähe" />
                  }
                  CardComponent={ActivityTeaser}
                  data={data.nearActivities!.items || []}
                />
              </ContentBlock>
            </Container>
          )}
        {store.config.renderConfig.poi.nearPois && data.nearPois?.total! > 0 && (
          <Container paddingTop={8}>
            <ContentBlock>
              <TeaserSlider
                TitleComponent={
                  <ContentTitle title="Weitere Tipps in der Nähe" />
                }
                CardComponent={PoiTeaser}
                data={data.nearPois!.items || []}
              />
            </ContentBlock>
          </Container>
        )}
        <Box marginBottom={12} />
      </Box>
    </Wrapper>
  );
};

export default Poi;
